import React from 'react';
import { ActionMeta, NonceProvider, Props } from 'react-select';
import { SelectTheme, SelectWrapper } from '../Select';
import {
    FlatGroupOptionType,
    flatGroupGetGroupKeyFromCompoundKey,
    flatGroupGetIsGroup,
} from './FlatGroupOptionType';
import { GroupValueContainer } from './GroupValueContainer';
import {
    GroupCheckStateEnum,
    getCheckStateForGroup,
    useReactSelectCollapsibleGroup,
} from './useReactSelectCollapsibleGroup';

type GroupSelectProps = Omit<Props<FlatGroupOptionType, true>, 'options'> & {
    label: string;
    value: FlatGroupOptionType[];
    options: FlatGroupOptionType[];
};
export function GroupSelect({
    options: propOptions = [],
    value: propValue = [],
    onChange: propOnChange,
    label: propLabel = '',
    ...props
}: GroupSelectProps) {
    // when it updates, use these groups to redraw the menu
    const [groupsInMenu, setGroupsInMenu] = React.useState<string[]>([]);

    // handles adding the options under the group
    const groupClickOptionLogic = React.useCallback(
        (o: 'add' | 'remove', entries: string[]) => {
            let newValues: string[] = [];

            if (o === 'add') {
                newValues = Array.from(new Set([...propValue.map((x) => x.value), ...entries]));
            } else {
                newValues = propValue
                    .map((x) => x.value)
                    .filter((x: string) => !entries.includes(x));
            }

            propOnChange?.(
                propOptions.filter?.((x) => newValues.includes(x.value)),
                {
                    action: 'select-option',
                    option: undefined,
                }
            );
        },
        [propOnChange, propOptions, propValue]
    );

    const { CheckOptionWithGroup, isGroupCollapsed } =
        useReactSelectCollapsibleGroup(groupClickOptionLogic);

    // if a "category" is selected then select its options
    const onChangeHandler = (v: FlatGroupOptionType[], a: ActionMeta<FlatGroupOptionType>) => {
        if (a.option && flatGroupGetIsGroup(a.option)) {
            const checkedState = getCheckStateForGroup(propValue ?? [], a.option.optionKeys);

            groupClickOptionLogic(
                checkedState === GroupCheckStateEnum.NONE ? 'add' : 'remove',
                a.option.optionKeys
            );
        } else {
            propOnChange?.(v, a);
        }
    };

    // quick-lookup to see what the label of the given key is for the options in a group.
    const subByKey = React.useMemo(
        () =>
            new Map<string, string>(
                propOptions?.filter?.((x) => !flatGroupGetIsGroup(x)).map((x) => [x.value, x.label])
            ),
        [propOptions]
    );

    // custom filter to hide group options based on collapse state
    const filter = (incomingOption: { data: FlatGroupOptionType }, inputValue: string) => {
        const lowerInputValue = inputValue.toLocaleLowerCase();
        const option = incomingOption.data;
        if (flatGroupGetIsGroup(option)) {
            return !!option?.optionKeys?.some((x) =>
                subByKey.get(x)?.toLocaleLowerCase().includes(lowerInputValue)
            );
        }
        return (
            (!isGroupCollapsed(option.groupKey as string) || inputValue !== '') &&
            !!option?.label.toLocaleLowerCase().includes(lowerInputValue)
        );
    };

    // array of currently selected group keys
    const possibleGroups = React.useMemo(
        () =>
            propValue
                .map((x: FlatGroupOptionType) => flatGroupGetGroupKeyFromCompoundKey(x.value))
                .filter?.(
                    (value: string, index: number, array: string[]) =>
                        array.indexOf(value) === index
                ) as string[],
        [propValue]
    );

    /**
     * Sort on menu close (when the menu is re-drawn).
     *
     * you have to move the group with the chunk of options under it
     * which this aims to do.
     *
     * This fixes the arrow keys so that selecting an options moves in order.
     */
    const sortedOptions = React.useMemo(
        () => [
            ...groupsInMenu.flatMap((x) =>
                propOptions.filter((y) =>
                    flatGroupGetIsGroup(y) ? y.value === x : y.groupKey === x
                )
            ),
            ...propOptions.filter(
                (u) => !groupsInMenu.includes(flatGroupGetGroupKeyFromCompoundKey(u.value))
            ),
        ],
        [groupsInMenu, propOptions]
    );

    // groups show up in the value picker first
    const groupsFirst = React.useMemo(
        () => [
            ...propOptions.filter(
                (x) =>
                    possibleGroups.includes(x.value) &&
                    getCheckStateForGroup(propValue as FlatGroupOptionType[], x.optionKeys) ===
                        GroupCheckStateEnum.ALL
            ),
            ...propValue.filter((x) => !flatGroupGetIsGroup(x)),
        ],
        [propOptions, possibleGroups, propValue]
    );

    return (
        <NonceProvider
            nonce={window.app.nonce}
            cacheKey={propLabel
                .toLowerCase()
                .replaceAll(' ', '')
                .replaceAll(/[^a-z]/g, '')}
        >
            <SelectWrapper
                {...props}
                label={propLabel}
                aria-label={propLabel}
                theme={SelectTheme}
                value={groupsFirst}
                options={sortedOptions}
                className={`grouped-select ${props.className ?? ''}`}
                components={{
                    ...props.components,
                    Option: CheckOptionWithGroup,
                    ValueContainer: GroupValueContainer,
                }}
                isMulti
                onKeyDown={(e: React.KeyboardEvent) => {
                    // clear the selection on backspace (we have a different value container)
                    if (e.key === 'Backspace' && !(e.target as HTMLInputElement)?.value) {
                        propOnChange?.([], { name: '', action: 'clear', removedValues: [] });
                        e.stopPropagation();
                        e.preventDefault();
                    }
                }}
                filterOption={filter}
                onMenuClose={() => {
                    setGroupsInMenu(possibleGroups ?? []);
                }}
                onChange={onChangeHandler}
                hideSelectedOptions={false}
                closeMenuOnSelect={false}
                tabSelectsValue={false}
                classNamePrefix="lrrSelect"
            />
        </NonceProvider>
    );
}
